import {
    formatIncomeSplitPercentages,
    type IncomeSplitConfigLegacy,
    IncomeSplitDetails,
    type IncomeSplitRuleLegacy,
    IncomeSplitRuleType,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import { AnalyticsUserEvent, type DepositAccount } from '@fruitful/types';
import type { EventArg, NavigationAction } from '@react-navigation/native';
import { Redirect, router, useNavigation } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import { cloneDeep, sortBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { NestableScrollContainer } from 'react-native-draggable-flatlist';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    Button,
    ButtonVariant,
    EditIcon,
    Icon,
    isWeb,
    PlusIcon,
    ScreenContainer,
    ScrollView,
    Spinner,
    styled,
    Switch,
    View,
    XStack,
    YStack,
} from 'smoothie';

import DashCurveShort from '../../../../assets/images/banking/income-split/dash-curve-short.svg';
import { uuid } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import useGetIncomeSplitFromGlobalState from '../../hooks/income-split/useGetIncomeSplitFromGlobalState';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useUpdateIncomeSplitStatus from '../../hooks/income-split/useUpdateIncomeSplitStatus';
import useGetAllBankAccountDetailsFromGlobalState from '../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import {
    incomeSplitAtomLegacy,
    incomeSplitRuleEditorModeAtom,
    selectedIncomeRuleAtomLegacy,
} from '../../state/PaycheckRoutingAtoms';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';
import { RegularLine, SortingLine } from './IncomeSplitLines';
import IncomeSplitRules from './IncomeSplitRules';
import IncomeSplitToggleSheet from './IncomeSplitToggleSheet';
import IncomeSplitWelcomeSheet from './info/IncomeSplitWelcome';

const IncomeSplitBox = styled(View, {
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 16,
    backgroundColor: '$gray200',
});

const Header = () => {
    return (
        <BodyMediumRegular color="$gray1000">
            Rules will execute in order from top to bottom subject to available
            funds.
        </BodyMediumRegular>
    );
};

const Toggle = ({ openToggleSheet }: { openToggleSheet: () => void }) => {
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtomLegacy);

    return (
        <IncomeSplitBox
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
        >
            <BodyMediumRegular>
                Income Split is{' '}
                {incomeSplit?.status === PaycheckRoutingStatus.ACTIVE
                    ? 'on'
                    : 'off'}
            </BodyMediumRegular>
            <Switch
                isChecked={incomeSplit?.status === PaycheckRoutingStatus.ACTIVE}
                onCheckedChange={(checked) => {
                    setIncomeSplit({
                        ...(incomeSplit as IncomeSplitConfigLegacy),
                        status: checked
                            ? PaycheckRoutingStatus.ACTIVE
                            : PaycheckRoutingStatus.INACTIVE,
                    });
                    openToggleSheet();
                }}
                style={{
                    transformOrigin: 'right',
                }}
            />
        </IncomeSplitBox>
    );
};

const Rules = ({
    incomeSplit,
    externalAccounts,
    onAddRule,
}: {
    incomeSplit: IncomeSplitConfigLegacy;
    externalAccounts: DepositAccount[];
    onAddRule: (rule: Partial<IncomeSplitRuleLegacy>) => void;
}) => {
    const [boxHeights, setBoxHeights] = useState<Record<string, number>>({});
    const [isSorting, setIsSorting] = useState(false);
    const [linesAnimated, setLinesAnimated] = useState(false);

    const visibleBoxHeights = Object.keys(boxHeights).reduce((acc, id) => {
        if (incomeSplit.rules.find((rule) => rule.id === id)) {
            acc[id] = boxHeights[id];
        }

        return acc;
    }, {});

    return (
        <XStack flex={1}>
            <YStack width={20} marginLeft={16} marginTop={-16}>
                {isSorting ? (
                    <SortingLine boxHeights={visibleBoxHeights} />
                ) : (
                    <RegularLine
                        incomeSplit={incomeSplit}
                        boxHeights={visibleBoxHeights}
                        isSorting={isSorting}
                        linesAnimated={linesAnimated}
                        setLinesAnimated={setLinesAnimated}
                        context="edit"
                    />
                )}
                {Object.keys(visibleBoxHeights).length ===
                    incomeSplit.rules.length &&
                    !isSorting && <DashCurveShort height={37} width={21} />}
            </YStack>
            <YStack flexShrink={1} flexGrow={1}>
                <IncomeSplitRules
                    incomeSplit={incomeSplit}
                    setBoxHeights={setBoxHeights}
                    setIsSorting={setIsSorting}
                    externalAccounts={externalAccounts}
                />
                <XStack
                    alignSelf="flex-start"
                    alignItems="center"
                    paddingHorizontal={8}
                    paddingVertical={8}
                    borderRadius={16}
                    backgroundColor="$gray200"
                    pressStyle={{ scale: 0.95 }}
                    animation="button"
                    onPress={() => {
                        // make new rule
                        onAddRule({
                            id: uuid(),
                            paycheckRoutingConfigId: incomeSplit.id,
                            type: IncomeSplitRuleType.PERCENTAGE,
                            order: incomeSplit.rules.length,
                            amount: 100,
                        });
                    }}
                    cursor="pointer"
                >
                    <View padding={4}>
                        <Icon icon={PlusIcon} />
                    </View>
                    <BodySmallEmphasized paddingHorizontal={8}>
                        Add rule
                    </BodySmallEmphasized>
                </XStack>
            </YStack>
        </XStack>
    );
};

const IncomeSplit = ({ welcome }: { welcome?: boolean }) => {
    const user = useAuthedUser();
    const { trackViewOnce } = useViewEvents();
    const navigation = useNavigation();
    const { incomeSplit, isLoading: isLoadingIncomeSplit } =
        useGetIncomeSplitFromGlobalState();
    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();
    const { mutateAsync: updateStatus } = useUpdateIncomeSplitStatus();

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalAndExternalDepositAccounts({});

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    const setIncomeSplit = useSetAtom(incomeSplitAtomLegacy);
    const setSelectedRule = useSetAtom(selectedIncomeRuleAtomLegacy);
    const setIncomeSplitRuleEditorMode = useSetAtom(
        incomeSplitRuleEditorModeAtom,
    );
    const [isWelcomeSheetOpen, setIsWelcomeSheetOpen] = useState(
        welcome ?? false,
    );
    const [isToggleSheetOpen, setIsToggleSheetOpen] = useState(false);

    // store a copy of the original income split configuration, so we can reset the income split atom
    // when going back without saving (and use both values to detect a diff and prompt members to take action)
    const originalIncomeSplit = useRef<IncomeSplitConfigLegacy | null>(null);
    useEffect(() => {
        if (incomeSplit && !originalIncomeSplit.current) {
            originalIncomeSplit.current = Object.freeze(cloneDeep(incomeSplit));
        }
    }, [incomeSplit]);

    // reset unsaved changes on going back via swiping or back button in header
    useEffect(() => {
        const handleBeforeRemove = (
            e: EventArg<'beforeRemove', true, { action: NavigationAction }>,
        ) => {
            if (
                e.data.action.type === 'GO_BACK' ||
                e.data.action.type === 'REPLACE'
            ) {
                setIncomeSplit(originalIncomeSplit.current);
            }
        };

        navigation.addListener('beforeRemove', handleBeforeRemove);

        return () => {
            navigation.removeListener('beforeRemove', handleBeforeRemove);
        };
    }, []);

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    if (!shouldSeeIncomeSplit) return <Redirect href="/" />;

    const hasChanges = (): boolean => {
        const stripIdsAndSort = (incomeSplit: IncomeSplitConfigLegacy) => {
            return {
                accounts: sortBy(
                    incomeSplit.accounts.map((account) => {
                        const { id, ...rest } = account;
                        return rest;
                    }),
                    'accountId',
                ),
                rules: sortBy(
                    incomeSplit.rules.map((rule) => {
                        const { id, ...rest } = rule;
                        return rest;
                    }),
                    'order',
                ),
            };
        };

        if (!incomeSplit || !originalIncomeSplit.current) return false;

        const rulesHaveChanged =
            JSON.stringify(stripIdsAndSort(incomeSplit)) !==
            JSON.stringify(stripIdsAndSort(originalIncomeSplit.current));
        const incomeTypeHasChanged =
            incomeSplit.onlyExecuteOnPaychecks !==
            originalIncomeSplit.current.onlyExecuteOnPaychecks;

        return rulesHaveChanged || incomeTypeHasChanged;
    };

    const updateIncomeSplitStatus = async (status: PaycheckRoutingStatus) => {
        await updateStatus(
            {
                body: {
                    status,
                },
                urlParams: {
                    configId: incomeSplit?.id as string,
                },
            },
            {
                onSuccess: (result) => {
                    const formattedIncomeSplit =
                        formatIncomeSplitPercentages(result);
                    originalIncomeSplit.current = formattedIncomeSplit;
                    setIncomeSplit(formattedIncomeSplit);
                    setIsToggleSheetOpen(false);
                },
            },
        );
    };

    const CustomScrollComponent = isWeb ? ScrollView : NestableScrollContainer;

    if (
        isLoadingIncomeSplit ||
        isLoadingBankAccountDetails ||
        !incomeSplit ||
        isLoadingDepositAccounts
    ) {
        return <Spinner />;
    }

    trackViewOnce(AnalyticsUserEvent.INCOME_SPLIT_RULES_VIEW);

    return (
        <>
            <ScreenContainer
                title="Income Split"
                hasBackButton={true}
                onBack={() => {
                    router.back();
                }}
                scrollContainerOverride={(children) => (
                    <CustomScrollComponent
                        showsVerticalScrollIndicator={isWeb}
                        contentContainerStyle={{
                            flexGrow: 1,
                            paddingHorizontal: 24,
                            paddingBottom: 24,
                        }}
                    >
                        {children}
                    </CustomScrollComponent>
                )}
            >
                <YStack flex={1} gap={24} justifyContent="space-between">
                    <YStack gap={16} flex={1} paddingTop={8}>
                        <Header />
                        {!incomeSplit.isNew && !hasChanges() && (
                            <Toggle
                                openToggleSheet={() => {
                                    setIsToggleSheetOpen(true);
                                }}
                            />
                        )}
                        <IncomeSplitDetails
                            incomeSplit={incomeSplit}
                            totalEligibleAccounts={bankAccountDetails.filter(
                                (account) =>
                                    isAccountValidAsIncomeSplitSource(
                                        user,
                                        account,
                                    ),
                            )}
                            icon={EditIcon}
                            onIconPress={() => {
                                router.navigate('/income-split/settings');
                            }}
                        />
                        <Rules
                            incomeSplit={incomeSplit}
                            externalAccounts={externalAccounts}
                            onAddRule={(
                                rule: Partial<IncomeSplitRuleLegacy>,
                            ) => {
                                setSelectedRule(rule);
                                setIncomeSplitRuleEditorMode('Add');
                                router.navigate('/income-split/edit');
                            }}
                        />
                    </YStack>

                    <Button
                        text="Continue"
                        variant={ButtonVariant.PRIMARY}
                        disabled={
                            !originalIncomeSplit.current ||
                            !hasChanges() ||
                            incomeSplit.rules.length === 0
                        }
                        onPress={() => {
                            router.navigate('/income-split/review');
                        }}
                    />
                </YStack>
            </ScreenContainer>
            <IncomeSplitWelcomeSheet
                isOpen={isWelcomeSheetOpen}
                onClose={() => {
                    setIsWelcomeSheetOpen(false);
                }}
            />
            <IncomeSplitToggleSheet
                isOpen={isToggleSheetOpen}
                onSave={updateIncomeSplitStatus}
                onClose={() => {
                    setIsToggleSheetOpen(false);
                }}
            />
        </>
    );
};

export default IncomeSplit;
